import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCalendarDays,
    faChevronLeft,
    faClock,
    faCrow,
    faExpand,
    faExternalLink,
    faImage,
    faLayerGroup,
    faLeaf,
    faMountain,
    faShieldHalved,
    faThumbsDown,
    faThumbsUp,
    faTree,
    faWater,
    faXmark
} from "@fortawesome/free-solid-svg-icons";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import {getHotspotDetail} from "../../../api/hotspot-detail";
import {submitHotspotHabitatFeedback} from "../../../api/hotspot-habitat-feedback";
import {debugWorldcoverLocation} from "../../../api/worldcover";
import {ExplorerHero, ExplorerHeroStat, ExplorerHeroStats} from "../../../components/explorer-hero";
import {buildHotspotHabitatProfile} from "../../../helpers/habitat-trust";
import {buildCountryPath, formatHotspotLocationLabel, getHotspotDetailHeroImage} from "../helpers";
import {capitalizeFirstLetter} from "../../../helpers/strings";
import {usePageTitle} from "../../../helpers/usePageTitle";
import type {HotspotExplorerProps} from "../types";
import {
    HOTSPOT_HERO_COMPACT_TITLE_LENGTH,
    HOTSPOT_DETAIL_TABS,
    HOTSPOT_HABITAT_FEEDBACK_SOMETHING_ELSE,
    INITIAL_MEDIA_COUNT,
    INITIAL_SPECIES_HIGHLIGHT_COUNT
} from "./constants";
import {
    formatObservationDate,
    formatObservationDay,
    getCachedHotspotMedia,
    getCachedHotspotMediaSummary,
    getCachedHotspotSpeciesHighlights,
    getCachedHotspotVisitHistory,
    getCachedLocationCoordinates,
    getHabitatRuleOptionGroups,
    getHabitatMetrics,
    getLocationSummary,
    getSpeciesSeenDateRange
} from "./helpers";
import {VisitHistoryChart} from "./visit-history-chart";
import './hotspot-detail.scss';
import type {WorldcoverDebugResponse} from "../../../api/worldcover";
import LoadingData from "../../loading-data";
import type {HotspotExplorerHotspot} from "../types";

type HotspotDetailTab = typeof HOTSPOT_DETAIL_TABS[number];

export function HotspotDetailView({
    hotspotExplorerIndex,
    isBirdDataHydrating,
    checklist,
    speciesImages,
    familyByTaxonomicOrder,
    parentTaxonomicOrderByTaxonomicOrder
}: HotspotExplorerProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const {locationId = ''} = useParams();
    const resolvedHotspotExplorerIndex = hotspotExplorerIndex!;
    const localHotspot = useMemo(() => resolvedHotspotExplorerIndex.hotspotsByLocationId.get(locationId), [resolvedHotspotExplorerIndex, locationId]);
    const [fallbackHotspot, setFallbackHotspot] = useState<HotspotExplorerHotspot>();
    const [fallbackHotspotLoading, setFallbackHotspotLoading] = useState(false);
    const [fallbackHotspotResolved, setFallbackHotspotResolved] = useState(false);
    const hotspot = useMemo(() => {
        const sourceHotspot = fallbackHotspot ?? localHotspot;
        if (!sourceHotspot) return undefined;

        if (!localHotspot) {
            return {
                ...sourceHotspot,
                observations: sourceHotspot.observations ?? [],
                visits: sourceHotspot.visits ?? 0,
                speciesSeen: sourceHotspot.speciesSeen ?? 0,
                lastVisited: sourceHotspot.lastVisited ?? '',
                lastVisitedMs: sourceHotspot.lastVisitedMs ?? 0,
                visitState: sourceHotspot.visitState ?? 'not_visited'
            };
        }

        return {
            ...sourceHotspot,
            observations: localHotspot.observations,
            visits: localHotspot.visits,
            speciesSeen: localHotspot.speciesSeen,
            lastVisited: localHotspot.lastVisited,
            lastVisitedMs: localHotspot.lastVisitedMs,
            visitState: 'visited'
        };
    }, [fallbackHotspot, localHotspot]);
    const [activeTab, setActiveTab] = useState<HotspotDetailTab>('overview');
    const [activeMediaCatalogNumber, setActiveMediaCatalogNumber] = useState<string>();
    const [worldcoverDebugResult, setWorldcoverDebugResult] = useState<WorldcoverDebugResponse>();
    const [worldcoverDebugLoading, setWorldcoverDebugLoading] = useState(false);
    const [worldcoverDebugError, setWorldcoverDebugError] = useState<string>();
    const [habitatFeedbackState, setHabitatFeedbackState] = useState<'correct' | 'incorrect' | undefined>();
    const [suggestedHabitat, setSuggestedHabitat] = useState('');
    const [suggestedHabitatRule, setSuggestedHabitatRule] = useState('');
    const [habitatFeedbackNotes, setHabitatFeedbackNotes] = useState('');
    const [habitatFeedbackSubmitting, setHabitatFeedbackSubmitting] = useState(false);
    const [habitatFeedbackError, setHabitatFeedbackError] = useState('');
    const [showHabitatFeedbackThanks, setShowHabitatFeedbackThanks] = useState(false);
    const [hasSubmittedHabitatFeedback, setHasSubmittedHabitatFeedback] = useState(false);
    const habitatFeedbackTimeoutRef = useRef<number>();
    const resolvedCountryCode = hotspot?.countryCode || hotspot?.regionData?.country?.code || 'US';
    const returnTo = (location.state as {returnTo?: string} | null | undefined)?.returnTo || buildCountryPath(resolvedCountryCode);
    const hotspotObservations = hotspot?.observations ?? [];
    const isVisitedHotspot = hotspot?.visitState === 'visited';
    const hotspotCoordinates = useMemo(() => {
        if (!hotspot) return undefined;
        const hotspotCenter = hotspot.center;
        if (hotspotCenter && Number.isFinite(hotspotCenter.latitude) && Number.isFinite(hotspotCenter.longitude)) {
            return {
                latitude: hotspotCenter.latitude,
                longitude: hotspotCenter.longitude
            };
        }
        if (Number.isFinite(hotspot.latitude) && Number.isFinite(hotspot.longitude)) {
            return {
                latitude: hotspot.latitude as number,
                longitude: hotspot.longitude as number
            };
        }

        return undefined;
    }, [hotspot]);
    const hotspotCenter = useMemo(
        () => hotspot ? getCachedLocationCoordinates(hotspotObservations) ?? hotspotCoordinates : undefined,
        [hotspot, hotspotCoordinates, hotspotObservations]
    );
    const useCompactHeroTitle = useMemo(
        () => (hotspot?.location?.trim().length ?? 0) > HOTSPOT_HERO_COMPACT_TITLE_LENGTH,
        [hotspot]
    );
    const heroImage = useMemo(
        () => getHotspotDetailHeroImage(hotspotCenter),
        [hotspotCenter]
    );
    const visitHistory = useMemo(() => hotspot && isVisitedHotspot ? getCachedHotspotVisitHistory(hotspotObservations) : [], [hotspot, hotspotObservations, isVisitedHotspot]);
    const locationSummary = useMemo(() => {
        if (!hotspot) return [];
        if (!isVisitedHotspot && hotspot.locationSummary?.length) {
            return hotspot.locationSummary;
        }

        const summaryObservations = hotspotObservations.length ? hotspotObservations : [{
            location: hotspot.location,
            locationId: hotspot.locationId,
            latitude: hotspotCenter?.latitude,
            longitude: hotspotCenter?.longitude
        } as any];

        return getLocationSummary(hotspot.locationId, summaryObservations, hotspot.ecoData, hotspot.regionData);
    }, [hotspot, hotspotCenter, hotspotObservations, isVisitedHotspot]);
    const habitatMetrics = useMemo(() => {
        if (!hotspot) return [];
        if (!isVisitedHotspot && Array.isArray(hotspot.habitatMetrics)) {
            return hotspot.habitatMetrics;
        }

        return getHabitatMetrics(hotspot.ecoData);
    }, [hotspot, isVisitedHotspot]);
    const habitatProfile = useMemo(() => hotspot ? buildHotspotHabitatProfile(hotspot as any) : undefined, [hotspot]);
    const isConfirmedHabitat = Boolean(habitatProfile?.sourceLabel);
    const isUnconfirmedHabitat = Boolean(habitatProfile && !habitatProfile.sourceLabel);
    const displayedHabitatLabel = habitatProfile && isUnconfirmedHabitat
        ? habitatProfile.displayHabitat.replace(/^Possibly\s+/, '')
        : habitatProfile?.displayHabitat;
    const classifierDebugPayload = useMemo(() => {
        if (!hotspot || !habitatProfile) return undefined;

        return {
            hotspot: {
                locationId: hotspot.locationId,
                location: hotspot.location,
                countryCode: hotspot.countryCode,
                stateProvince: hotspot.stateProvince,
                county: hotspot.county,
                latitude: hotspotCenter?.latitude,
                longitude: hotspotCenter?.longitude
            },
            ecoData: {
                habitat_local_primary: hotspot.ecoData?.habitat_local_primary,
                habitat_local_secondary: hotspot.ecoData?.habitat_local_secondary,
                habitat_local_mix: hotspot.ecoData?.habitat_local_mix,
                habitat_local_sample_count: hotspot.ecoData?.habitat_local_sample_count,
                habitat_local_h3_cells: hotspot.ecoData?.habitat_local_h3_cells,
                habitat_local_method: hotspot.ecoData?.habitat_local_method,
                habitat_local_radius_m: hotspot.ecoData?.habitat_local_radius_m,
                habitat_primary: hotspot.ecoData?.habitat_primary,
                habitat_secondary: hotspot.ecoData?.habitat_secondary,
                habitat_mix: hotspot.ecoData?.habitat_mix,
                habitat_group: hotspot.ecoData?.habitat_group,
                habitat_dominant_worldcover_label: hotspot.ecoData?.habitat_dominant_worldcover_label,
                worldcover_label: hotspot.ecoData?.worldcover_label,
                biomeName: hotspot.ecoData?.biomeName,
                ecoregionName: hotspot.ecoData?.ecoregionName,
                realm: hotspot.ecoData?.realm,
                terrain_elevation_m: hotspot.ecoData?.terrain_elevation_m,
                terrain_elevation_band: hotspot.ecoData?.terrain_elevation_band,
                water_distance_m: hotspot.ecoData?.water_distance_m,
                water_proximity_label: hotspot.ecoData?.water_proximity_label,
                coastline_source: hotspot.ecoData?.coastline_source,
                coastline_source_layer: hotspot.ecoData?.coastline_source_layer,
                coastline_source_scale: hotspot.ecoData?.coastline_source_scale,
                coastline_source_version: hotspot.ecoData?.coastline_source_version,
                distance_to_coast_m: hotspot.ecoData?.distance_to_coast_m,
                coastal_context: hotspot.ecoData?.coastal_context,
                is_coastal: hotspot.ecoData?.is_coastal,
                is_near_coastal: hotspot.ecoData?.is_near_coastal,
                coastline_confidence: hotspot.ecoData?.coastline_confidence,
                marine_context: hotspot.ecoData?.marine_context,
            },
            classifier: {
                primaryHabitat: habitatProfile.primaryHabitat,
                habitatFamily: habitatProfile.habitatFamily,
                confidence: habitatProfile.confidence,
                secondaryHabitats: habitatProfile.secondaryHabitats,
                terrainSummary: habitatProfile.terrainSummary,
                waterContext: habitatProfile.waterContext,
                reasons: habitatProfile.reasons,
                explanation: habitatProfile.explanation,
            }
        };
    }, [habitatProfile, hotspot, hotspotCenter]);
    const speciesHighlights = useMemo(() => hotspot && isVisitedHotspot ? getCachedHotspotSpeciesHighlights(
        hotspotObservations,
        checklist,
        speciesImages,
        familyByTaxonomicOrder,
        parentTaxonomicOrderByTaxonomicOrder
    ) : [], [checklist, familyByTaxonomicOrder, hotspot, hotspotObservations, isVisitedHotspot, parentTaxonomicOrderByTaxonomicOrder, speciesImages]);
    const mediaItems = useMemo(() => hotspot && isVisitedHotspot ? getCachedHotspotMedia(hotspotObservations) : [], [hotspot, hotspotObservations, isVisitedHotspot]);
    const mediaSummary = useMemo(() => getCachedHotspotMediaSummary(mediaItems), [mediaItems]);
    const habitatChartBackground = useMemo(() => {
        if (!habitatMetrics.length) return undefined;

        let currentPercent = 0;
        const segments = habitatMetrics.map((metric, index) => {
            const startPercent = currentPercent;
            currentPercent += metric.percent;
            return `${metric.color} ${startPercent}% ${currentPercent}%`;
        });

        return `conic-gradient(${segments.join(', ')})`;
    }, [habitatMetrics]);
    const overviewSpecies = speciesHighlights.slice(0, INITIAL_SPECIES_HIGHLIGHT_COUNT);
    const overviewMedia = mediaItems.slice(0, INITIAL_MEDIA_COUNT);
    const selectedOverviewMediaCatalogNumber = activeMediaCatalogNumber && overviewMedia.some(item => item.catalogNumber === activeMediaCatalogNumber)
        ? activeMediaCatalogNumber
        : overviewMedia[0]?.catalogNumber;
    const selectedOverviewMedia = overviewMedia.find(item => item.catalogNumber === selectedOverviewMediaCatalogNumber);
    const selectedFullMediaCatalogNumber = activeMediaCatalogNumber && mediaItems.some(item => item.catalogNumber === activeMediaCatalogNumber)
        ? activeMediaCatalogNumber
        : mediaItems[0]?.catalogNumber;
    const selectedFullMedia = mediaItems.find(item => item.catalogNumber === selectedFullMediaCatalogNumber);
    const habitatFeedbackChoices = useMemo(() => {
        if (!habitatProfile) return [HOTSPOT_HABITAT_FEEDBACK_SOMETHING_ELSE];

        const choices = [
            ...habitatProfile.explanation.nextMatches.map(match => match.label),
            HOTSPOT_HABITAT_FEEDBACK_SOMETHING_ELSE
        ].filter(Boolean);

        return Array.from(new Set(choices));
    }, [habitatProfile]);
    const habitatRuleOptionGroups = useMemo(
        () => getHabitatRuleOptionGroups(),
        []
    );

    useEffect(() => {
        let isActive = true;

        const resolveFallbackHotspot = async () => {
            setFallbackHotspot(undefined);

            if (!locationId || isBirdDataHydrating) {
                setFallbackHotspotResolved(Boolean(localHotspot));
                setFallbackHotspotLoading(false);
                return;
            }

            setFallbackHotspotLoading(true);
            setFallbackHotspotResolved(false);

            try {
                const response = await getHotspotDetail(locationId);
                if (!isActive) return;

                setFallbackHotspot(response.hotspot);
            } catch (error) {
                if (!isActive) return;

                setFallbackHotspot(undefined);
            } finally {
                if (!isActive) return;

                setFallbackHotspotLoading(false);
                setFallbackHotspotResolved(true);
            }
        };

        void resolveFallbackHotspot();

        return () => {
            isActive = false;
        };
    }, [isBirdDataHydrating, localHotspot, locationId]);

    usePageTitle(hotspot?.location ?? 'Hotspot Detail', 'Hotspot Explorer');
    const isCustomHabitatSelection = suggestedHabitat === HOTSPOT_HABITAT_FEEDBACK_SOMETHING_ELSE;
    const showHabitatFeedbackAcknowledgement = () => {
        if (habitatFeedbackTimeoutRef.current) {
            window.clearTimeout(habitatFeedbackTimeoutRef.current);
        }

        setHabitatFeedbackState(undefined);
        setSuggestedHabitat('');
        setSuggestedHabitatRule('');
        setHabitatFeedbackNotes('');
        setHasSubmittedHabitatFeedback(true);
        setShowHabitatFeedbackThanks(true);
        habitatFeedbackTimeoutRef.current = window.setTimeout(() => {
            setShowHabitatFeedbackThanks(false);
            habitatFeedbackTimeoutRef.current = undefined;
        }, 3000);
    };
    const submitHabitatFeedback = async (feedbackType: 'confirm' | 'correct') => {
        if (!hotspot || !hotspotCenter || !habitatProfile || hasSubmittedHabitatFeedback || habitatFeedbackSubmitting) {
            return;
        }

        const resolvedSuggestedHabitat = feedbackType === 'correct'
            ? (isCustomHabitatSelection ? suggestedHabitatRule : suggestedHabitat).trim()
            : '';

        if (feedbackType === 'correct' && resolvedSuggestedHabitat === '') {
            setHabitatFeedbackError(
                isCustomHabitatSelection
                    ? 'Choose a habitat from More habitat options before submitting.'
                    : 'Choose a suggested habitat before submitting.'
            );
            return;
        }

        setHabitatFeedbackSubmitting(true);
        setHabitatFeedbackError('');

        try {
            await submitHotspotHabitatFeedback({
                feedbackType,
                locationId,
                hotspotName: hotspot.location,
                latitude: hotspotCenter.latitude,
                longitude: hotspotCenter.longitude,
                classifierVersion: String(habitatProfile.version),
                predictedHabitat: habitatProfile.primaryHabitat,
                predictedFamily: habitatProfile.habitatFamily,
                predictedConfidence: habitatProfile.confidence,
                suggestedHabitat: feedbackType === 'correct' ? resolvedSuggestedHabitat : undefined,
                suggestedRuleLabel: feedbackType === 'correct' && isCustomHabitatSelection ? suggestedHabitatRule : undefined,
                notes: habitatFeedbackNotes.trim() || undefined,
                pageRoute: `${location.pathname}${location.search}`,
                pageUrl: window.location.href,
                predictionContext: {
                    displayHabitat: habitatProfile.displayHabitat,
                    primaryHabitat: habitatProfile.primaryHabitat,
                    habitatFamily: habitatProfile.habitatFamily,
                    confidence: habitatProfile.confidence,
                    secondaryHabitats: habitatProfile.secondaryHabitats,
                    terrainSummary: habitatProfile.terrainSummary,
                    waterContext: habitatProfile.waterContext,
                    reasons: habitatProfile.reasons,
                    sourceFields: habitatProfile.sourceFields,
                    explanation: habitatProfile.explanation,
                },
                hotspotContext: {
                    hotspotName: hotspot.location,
                    countryCode: hotspot.countryCode,
                    latitude: hotspotCenter.latitude,
                    longitude: hotspotCenter.longitude,
                    ecoregionName: hotspot.ecoData?.ecoregionName ?? null,
                    biomeName: hotspot.ecoData?.biomeName ?? null,
                    habitatPrimary: hotspot.ecoData?.habitat_local_primary ?? hotspot.ecoData?.habitat_primary ?? null,
                    habitatSecondary: hotspot.ecoData?.habitat_local_secondary ?? hotspot.ecoData?.habitat_secondary ?? [],
                    habitatMix: hotspot.ecoData?.habitat_local_mix ?? hotspot.ecoData?.habitat_mix ?? null,
                }
            });

            showHabitatFeedbackAcknowledgement();
        } catch (error) {
            setHabitatFeedbackError(error instanceof Error ? error.message : 'Habitat feedback submission failed.');
        } finally {
            setHabitatFeedbackSubmitting(false);
        }
    };
    const dismissHabitatCorrection = () => {
        setHabitatFeedbackState(undefined);
        setSuggestedHabitat('');
        setSuggestedHabitatRule('');
        setHabitatFeedbackNotes('');
        setHabitatFeedbackError('');
    };

    useEffect(() => {
        setActiveTab('overview');
        setActiveMediaCatalogNumber(undefined);
        setWorldcoverDebugResult(undefined);
        setWorldcoverDebugError(undefined);
        setWorldcoverDebugLoading(false);
        setHabitatFeedbackState(undefined);
        setSuggestedHabitat('');
        setSuggestedHabitatRule('');
        setHabitatFeedbackNotes('');
        setHabitatFeedbackSubmitting(false);
        setHabitatFeedbackError('');
        setShowHabitatFeedbackThanks(false);
        setHasSubmittedHabitatFeedback(false);
    }, [locationId]);

    useEffect(() => () => {
        if (habitatFeedbackTimeoutRef.current) {
            window.clearTimeout(habitatFeedbackTimeoutRef.current);
        }
    }, []);

    const loadWorldcoverDebug = async () => {
        if (!hotspotCenter) {
            setWorldcoverDebugError('No coordinates are available for this hotspot.');
            return;
        }

        setWorldcoverDebugLoading(true);
        setWorldcoverDebugError(undefined);

        try {
            const locationLabel = `${hotspotCenter.latitude},${hotspotCenter.longitude}`;
            const result = await debugWorldcoverLocation({
                location: {
                    latitude: hotspotCenter.latitude,
                    longitude: hotspotCenter.longitude,
                    locationLabel
                }
            });

            setWorldcoverDebugResult(result);
        } catch (error) {
            setWorldcoverDebugResult(undefined);
            setWorldcoverDebugError(error instanceof Error ? error.message : 'WorldCover debug request failed.');
        } finally {
            setWorldcoverDebugLoading(false);
        }
    };

    if ((!hotspot && isBirdDataHydrating) || fallbackHotspotLoading) {
        return (
            <LoadingData
                title="Loading hotspot detail..."
                message="Matching your hotspot against local and cached hotspot data."
            />
        );
    }

    if (!hotspot && !fallbackHotspotResolved) {
        return (
            <LoadingData
                title="Loading hotspot detail..."
                message="Looking up hotspot details."
            />
        );
    }

    if (!hotspot && fallbackHotspotResolved) {
        return (
            <section className="hotspot-detail">
                <button type="button" className="hotspot-detail__back" onClick={() => navigate(returnTo)}>
                    <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                    Back
                </button>
                <div className="hotspot-detail__empty">
                    <h1>Hotspot Detail</h1>
                    <p>This hotspot could not be found.</p>
                </div>
            </section>
        );
    }

    const resolvedHotspot = hotspot as HotspotExplorerHotspot;

    const renderSpeciesGrid = (speciesList: typeof speciesHighlights, fullWidth = false) => {
        if (!speciesList.length) {
            return <p className="hotspot-detail__muted">No species observations are available for this hotspot.</p>;
        }

        return (
            <div className={`hotspot-detail__species-grid ${fullWidth ? 'hotspot-detail__species-grid--fullwidth' : ''}`.trim()}>
                {speciesList.map(species => {
                    const speciesBody = (
                        <>
                            <img src={species.imageUrl} alt="" />
                            <div className="hotspot-detail__species-body">
                                <strong>{species.commonName}</strong>
                                <em>{species.scientificName || 'Species detail unavailable'}</em>
                                <div className="hotspot-detail__species-meta">
                                    <span>
                                        {species.observationCount.toLocaleString()} {species.observationCount === 1 ? 'observation' : 'observations'}
                                    </span>
                                </div>
                                <small>
                                    {species.lastSeen ? `Last seen ${formatObservationDay(species.lastSeen)}` : 'Date unavailable'}
                                </small>
                            </div>
                        </>
                    );

                    return species.detailPath ? (
                        <Link className="hotspot-detail__species-card" to={species.detailPath} key={species.taxonomicOrder}>
                            {speciesBody}
                        </Link>
                    ) : (
                        <div className="hotspot-detail__species-card" key={species.taxonomicOrder}>
                            {speciesBody}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderMediaBrowser = (items: typeof mediaItems, selectedMediaItem: typeof mediaItems[number] | undefined, fullWidth = false) => {
        if (!selectedMediaItem) {
            return <p className="hotspot-detail__muted">No Macaulay media is attached to observations from this hotspot yet.</p>;
        }

        return (
            <div className={`hotspot-detail__media-browser ${fullWidth ? 'hotspot-detail__media-browser--fullwidth' : ''}`.trim()}>
                <iframe
                    className="hotspot-detail__media-embed"
                    src={`https://macaulaylibrary.org/asset/${selectedMediaItem.catalogNumber}/embed`}
                    title={`Macaulay Library asset ML${selectedMediaItem.catalogNumber}`}
                    loading="lazy"
                />
                <div className="hotspot-detail__media-side">
                    <ul className="hotspot-detail__media-list">
                        {items.map(item => (
                            <li key={item.catalogNumber}>
                                <button
                                    type="button"
                                    className={item.catalogNumber === selectedMediaItem.catalogNumber ? 'is-active' : ''}
                                    onClick={() => setActiveMediaCatalogNumber(item.catalogNumber)}
                                >
                                    <FontAwesomeIcon icon={faImage} aria-hidden="true" />
                                    <span className="hotspot-detail__media-list-label">ML{item.catalogNumber}</span>
                                    <small className="hotspot-detail__media-list-species">{item.observation.commonName || 'Unknown species'}</small>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        );
    };

    return (
        <section className="hotspot-detail">
            <ExplorerHero
                className={`hotspot-detail__hero ${useCompactHeroTitle ? 'hotspot-detail__hero--compact-title' : ''}`.trim()}
                backgroundImage={heroImage}
                backLabel="Back"
                onBack={() => navigate(returnTo)}
                eyebrow="Hotspot Explorer"
                title={resolvedHotspot.location}
                intro={resolvedHotspot.locationLabel || formatHotspotLocationLabel(resolvedHotspot)}
            >
                <ExplorerHeroStats ariaLabel="Hotspot summary">
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faClock} />}
                        label="Visits"
                        value={isVisitedHotspot ? resolvedHotspot.visits.toLocaleString() : '--'}
                    />
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faCrow} />}
                        label="Species Seen"
                        value={isVisitedHotspot ? resolvedHotspot.speciesSeen.toLocaleString() : '--'}
                    />
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faCalendarDays} />}
                        label="Last Visited"
                        value={isVisitedHotspot ? resolvedHotspot.lastVisited || '--' : 'Not visited'}
                    />
                </ExplorerHeroStats>
            </ExplorerHero>

            <div className="hotspot-detail__tabs" role="tablist" aria-label="Hotspot detail sections">
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'overview'}
                    className={`hotspot-detail__tab ${activeTab === 'overview' ? 'is-active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                {isVisitedHotspot ? (
                    <>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'species'}
                            className={`hotspot-detail__tab ${activeTab === 'species' ? 'is-active' : ''}`}
                            onClick={() => setActiveTab('species')}
                        >
                            Species
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'media'}
                            className={`hotspot-detail__tab ${activeTab === 'media' ? 'is-active' : ''}`}
                            onClick={() => setActiveTab('media')}
                        >
                            Media
                        </button>
                    </>
                ) : null}
            </div>

            {activeTab === 'overview' ? (
                <div className="hotspot-detail__content">
                    <article className="hotspot-detail__card hotspot-detail__card--habitat">
                        <div className="hotspot-detail__card-heading">
                            <h2>Habitat at a Glance</h2>
                        </div>
                        <div className="hotspot-detail__habitat-overview">
                            <div className="hotspot-detail__habitat-copy">
                                <span className="hotspot-detail__card-kicker">
                                    {isUnconfirmedHabitat ? 'Habitat Classification (Best Guess)' : 'Habitat Classification'}
                                </span>
                                {habitatProfile ? (
                                    <>
                                        <div className="hotspot-detail__classification-summary" aria-label="Birding habitat classification">
                                            <div className="hotspot-detail__classification-tile">
                                                <span className="hotspot-detail__classification-icon" aria-hidden="true">
                                                    <FontAwesomeIcon icon={faLayerGroup} />
                                                </span>
                                                <div className="hotspot-detail__classification-copy">
                                                    <span>Habitat Family</span>
                                                    <strong>{capitalizeFirstLetter(habitatProfile.habitatFamily)}</strong>
                                                </div>
                                            </div>
                                            <div className="hotspot-detail__classification-tile">
                                                <span className="hotspot-detail__classification-icon" aria-hidden="true">
                                                    <FontAwesomeIcon
                                                        icon={
                                                            habitatProfile.habitatFamily === 'Forest'
                                                                ? faTree
                                                                : habitatProfile.habitatFamily === 'Water' || habitatProfile.habitatFamily === 'Wetland'
                                                                    ? faWater
                                                                    : habitatProfile.habitatFamily === 'Mountain'
                                                                        ? faMountain
                                                                        : faLeaf
                                                        }
                                                    />
                                                </span>
                                                <div className="hotspot-detail__classification-copy">
                                                    <span>Birding Habitat</span>
                                                    <strong>{displayedHabitatLabel}</strong>
                                                    {!habitatProfile.sourceLabel ? (
                                                        <div className="hotspot-detail__classification-alternates">
                                                            <span className="hotspot-detail__classification-kicker">Next Best Matches</span>
                                                            {habitatProfile.explanation.nextMatches.length ? (
                                                                <div className="hotspot-detail__classification-alternate-list">
                                                                    {habitatProfile.explanation.nextMatches.map(match => (
                                                                        <small key={match.id}>{match.label}</small>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <small>None</small>
                                                            )}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                        {!isConfirmedHabitat ? (
                                            <p className="hotspot-detail__classification-confidence">
                                                <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
                                                Confidence: {capitalizeFirstLetter(habitatProfile.confidence)}
                                            </p>
                                        ) : null}
                                        {habitatProfile.sourceLabel ? (
                                            <p className="hotspot-detail__classification-confidence">
                                                <FontAwesomeIcon icon={faLayerGroup} aria-hidden="true" />
                                                Source: {habitatProfile.sourceLabel}
                                            </p>
                                        ) : null}
                                        <div className="hotspot-detail__habitat-feedback" aria-label="Habitat correction mockup">
                                            <div className="hotspot-detail__habitat-feedback-copy">
                                                <strong>Does this look correct?</strong>
                                            </div>
                                            <div className="hotspot-detail__habitat-feedback-actions">
                                                <button
                                                    type="button"
                                                    className={`hotspot-detail__habitat-feedback-toggle hotspot-detail__habitat-feedback-toggle--positive ${habitatFeedbackState === 'correct' ? 'is-active' : ''}`}
                                                    disabled={hasSubmittedHabitatFeedback || habitatFeedbackSubmitting}
                                                    onClick={() => void submitHabitatFeedback('confirm')}
                                                >
                                                    <FontAwesomeIcon icon={faThumbsUp} aria-hidden="true" />
                                                    Looks right
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`hotspot-detail__habitat-feedback-toggle hotspot-detail__habitat-feedback-toggle--negative ${habitatFeedbackState === 'incorrect' ? 'is-active' : ''}`}
                                                    disabled={hasSubmittedHabitatFeedback || habitatFeedbackSubmitting}
                                                    onClick={() => {
                                                        setHabitatFeedbackState('incorrect');
                                                        setSuggestedHabitat('');
                                                        setSuggestedHabitatRule('');
                                                        setHabitatFeedbackNotes('');
                                                        setHabitatFeedbackError('');
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faThumbsDown} aria-hidden="true" />
                                                    Incorrect habitat
                                                </button>
                                            </div>
                                            {habitatFeedbackError ? (
                                                <p className="hotspot-detail__habitat-feedback-error">{habitatFeedbackError}</p>
                                            ) : null}
                                            {showHabitatFeedbackThanks ? (
                                                <div className="hotspot-detail__habitat-feedback-confirmation">
                                                    <span>Thanks for giving feedback!</span>
                                                </div>
                                            ) : null}
                                            {habitatFeedbackState === 'incorrect' ? (
                                                <div className="hotspot-detail__habitat-feedback-form">
                                                    <div className="hotspot-detail__habitat-feedback-form-header">
                                                        <strong>Suggest a correction</strong>
                                                        <button
                                                            type="button"
                                                            className="hotspot-detail__habitat-feedback-close"
                                                            onClick={dismissHabitatCorrection}
                                                            aria-label="Dismiss habitat correction form"
                                                        >
                                                            <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                                                        </button>
                                                    </div>
                                                    <label className="hotspot-detail__habitat-feedback-field">
                                                        <span>Suggested habitat</span>
                                                        <select
                                                            className="form-select"
                                                            value={suggestedHabitat}
                                                            onChange={(event) => {
                                                                setSuggestedHabitat(event.target.value);
                                                                if (event.target.value !== HOTSPOT_HABITAT_FEEDBACK_SOMETHING_ELSE) {
                                                                    setSuggestedHabitatRule('');
                                                                }
                                                            }}
                                                        >
                                                            <option value="">Select the habitat you believe is correct</option>
                                                            {habitatFeedbackChoices.map(option => (
                                                                <option value={option} key={option}>
                                                                    {option === HOTSPOT_HABITAT_FEEDBACK_SOMETHING_ELSE ? 'Something else' : option}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                    {isCustomHabitatSelection ? (
                                                        <label className="hotspot-detail__habitat-feedback-field">
                                                            <span>More habitat options</span>
                                                            <select
                                                                className="form-select"
                                                                value={suggestedHabitatRule}
                                                                onChange={(event) => setSuggestedHabitatRule(event.target.value)}
                                                            >
                                                                <option value="">Select a habitat</option>
                                                                {habitatRuleOptionGroups.map(group => (
                                                                    <optgroup label={group.label} key={group.label}>
                                                                        {group.options.map(option => (
                                                                            <option value={option.value} key={option.value}>
                                                                                {option.label}
                                                                            </option>
                                                                        ))}
                                                                    </optgroup>
                                                                ))}
                                                            </select>
                                                        </label>
                                                    ) : null}
                                                    <label className="hotspot-detail__habitat-feedback-field">
                                                        <span>Notes</span>
                                                        <textarea
                                                            className="form-control"
                                                            rows={3}
                                                            value={habitatFeedbackNotes}
                                                            onChange={(event) => setHabitatFeedbackNotes(event.target.value)}
                                                            placeholder="Why do you think the current habitat is incorrect?"
                                                        />
                                                    </label>
                                                    <div className="hotspot-detail__habitat-feedback-submit-row">
                                                        <button
                                                            type="button"
                                                            className="hotspot-detail__inline-action"
                                                            disabled={habitatFeedbackSubmitting || hasSubmittedHabitatFeedback}
                                                            onClick={() => void submitHabitatFeedback('correct')}
                                                        >
                                                            {habitatFeedbackSubmitting ? 'Saving...' : 'Save correction'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    </>
                                ) : null}
                            </div>
                            {habitatMetrics.length ? (
                                <div className="hotspot-detail__habitat-chart-wrap" aria-label="Habitat composition">
                                    <div
                                        className="hotspot-detail__habitat-chart"
                                        aria-hidden="true"
                                        style={habitatChartBackground ? {background: habitatChartBackground} : undefined}
                                    >
                                        <span />
                                    </div>
                                    <ul className="hotspot-detail__habitat-legend">
                                        {habitatMetrics.map((metric, index) => (
                                        <li className="hotspot-detail__habitat-legend-item" key={metric.label}>
                                            <span
                                                className="hotspot-detail__habitat-legend-swatch"
                                                aria-hidden="true"
                                                style={{background: metric.color}}
                                            />
                                                <strong>{metric.label}</strong>
                                                <span>{metric.percent}%</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <div className="hotspot-detail__habitat-chart-wrap" aria-label="WorldCover unavailable">
                                    <div className="hotspot-detail__habitat-chart-placeholder">
                                        <span>No WorldCover data is available for this hotspot.</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {habitatProfile ? (
                            <div className="hotspot-detail__habitat-explanation" aria-label="Birding habitat decision details">
                                <details className="hotspot-detail__habitat-explanation-json">
                                    <summary>View Hotspot Classifier Data</summary>
                                    <pre>{JSON.stringify(habitatProfile.explanation.debug, null, 2)}</pre>
                                </details>
                            </div>
                        ) : null}
                    </article>

                    <article className="hotspot-detail__card hotspot-detail__card--summary">
                        <div className="hotspot-detail__card-heading">
                            <h2>Location Summary</h2>
                        </div>
                        <dl className="hotspot-detail__summary">
                            {locationSummary.map(item => (
                                <div key={item.label}>
                                    <dt>{item.label}</dt>
                                    <dd>
                                        {item.href ? (
                                            <a
                                                className="hotspot-detail__summary-link"
                                                href={item.href}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <span>{item.value}</span>
                                                <FontAwesomeIcon icon={faExternalLink} aria-hidden="true" />
                                            </a>
                                        ) : item.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </article>

                    <article className="hotspot-detail__card hotspot-detail__card--species">
                        <div className="hotspot-detail__card-heading hotspot-detail__card-heading--split">
                            <div>
                                <h2>Species Highlights ({speciesHighlights.length})</h2>
                                <span className="hotspot-detail__card-kicker">{getSpeciesSeenDateRange(speciesHighlights)}</span>
                            </div>
                            {speciesHighlights.length > INITIAL_SPECIES_HIGHLIGHT_COUNT ? (
                                <button
                                    type="button"
                                    className="hotspot-detail__inline-action"
                                    onClick={() => setActiveTab('species')}
                                >
                                    Show All Species
                                    <FontAwesomeIcon icon={faExpand} aria-hidden="true" />
                                </button>
                            ) : null}
                        </div>
                        {isVisitedHotspot
                            ? renderSpeciesGrid(overviewSpecies)
                            : <p className="hotspot-detail__muted">No personal species history is loaded for this hotspot yet.</p>}
                    </article>

                    <article className="hotspot-detail__card hotspot-detail__card--media">
                        <div className="hotspot-detail__card-heading hotspot-detail__card-heading--split">
                            <div>
                                <h2>Recent Media</h2>
                                <span className="hotspot-detail__card-kicker">
                                    {mediaSummary.itemCount.toLocaleString()} assets across {mediaSummary.speciesCount.toLocaleString()} species
                                </span>
                            </div>
                            {mediaItems.length > INITIAL_MEDIA_COUNT ? (
                                <button
                                    type="button"
                                    className="hotspot-detail__inline-action"
                                    onClick={() => setActiveTab('media')}
                                >
                                    Show All Media
                                    <FontAwesomeIcon icon={faExpand} aria-hidden="true" />
                                </button>
                            ) : null}
                        </div>
                        {isVisitedHotspot
                            ? renderMediaBrowser(overviewMedia, selectedOverviewMedia)
                            : <p className="hotspot-detail__muted">No personal media history is loaded for this hotspot yet.</p>}
                    </article>

                    <article className="hotspot-detail__card hotspot-detail__card--visits">
                        <h2>Visit History</h2>
                        {isVisitedHotspot ? <VisitHistoryChart observations={hotspotObservations} /> : (
                            <p className="hotspot-detail__muted">This hotspot is cached in BirdMap Explorer, but it is not part of your loaded visit history.</p>
                        )}

                        {isVisitedHotspot && visitHistory.length ? (
                            <section className="hotspot-detail__recent-visits" aria-label="Recent visits">
                                <h3>Recent Visits</h3>
                                <ol>
                                    {visitHistory.slice(0, 5).map(visit => (
                                        <li key={visit.visitKey}>
                                            <time>{formatObservationDate(visit.date, visit.time)}</time>
                                            <strong>{visit.location}</strong>
                                            <span>{visit.speciesCount.toLocaleString()} species</span>
                                            <b>{visit.observationCount.toLocaleString()} observations</b>
                                        </li>
                                    ))}
                                </ol>
                            </section>
                        ) : null}
                    </article>

{/*
                    <article className="hotspot-detail__card hotspot-detail__card--conservation">
                        <div className="hotspot-detail__card-heading">
                            <h2>Conservation Context</h2>
                            <span className="hotspot-detail__card-kicker">
                                <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
                                Mocked layout pending a data source
                            </span>
                        </div>
                        <div className="hotspot-detail__conservation-list">
                            {HOTSPOT_CONSERVATION_ITEMS.map(item => (
                                <div className="hotspot-detail__conservation-item" key={item.label}>
                                    <strong>{item.label}</strong>
                                    <span>{item.value}</span>
                                    <small>{item.detail}</small>
                                </div>
                            ))}
                        </div>
                    </article>
*/}
                </div>
            ) : null}

            {activeTab === 'species' ? (
                <div className="hotspot-detail__tab-panel">
                    <article className="hotspot-detail__card hotspot-detail__card--fullwidth">
                        <div className="hotspot-detail__card-heading">
                            <h2>All Species ({speciesHighlights.length})</h2>
                            <span className="hotspot-detail__card-kicker">{getSpeciesSeenDateRange(speciesHighlights)}</span>
                        </div>
                        {isVisitedHotspot
                            ? renderSpeciesGrid(speciesHighlights, true)
                            : <p className="hotspot-detail__muted">No personal species history is loaded for this hotspot yet.</p>}
                    </article>
                </div>
            ) : null}

            {activeTab === 'media' ? (
                <div className="hotspot-detail__tab-panel">
                    <article className="hotspot-detail__card hotspot-detail__card--fullwidth">
                        <div className="hotspot-detail__card-heading">
                            <h2>Media at this Location</h2>
                            <span className="hotspot-detail__card-kicker">
                                {mediaSummary.itemCount.toLocaleString()} assets across {mediaSummary.speciesCount.toLocaleString()} species
                            </span>
                        </div>
                        {isVisitedHotspot
                            ? renderMediaBrowser(mediaItems, selectedFullMedia, true)
                            : <p className="hotspot-detail__muted">No personal media history is loaded for this hotspot yet.</p>}
                    </article>
                </div>
            ) : null}
        </section>
    );
}
